import React, { useMemo, useState, useRef, useEffect } from 'react'
import { ICustomerWithFavorites } from '../types/customerWithFavorites'
import styled from 'styled-components'
import useDebounce from '../../../main/second/mainbar/seller/Helpers/useDebounce'
import { SearchInput } from '../../ui/SearchInput/SearchInput'
import CustomerFavoritesItem from './CustomerFavoritesItem'
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import StarIcon from '../../icons/IconLib/StarIcon'

interface IModalCustomerFavoritesProps {
  list: ICustomerWithFavorites[],
  isLoading: boolean,
  createFavorite: (customerId: number) => void,
  deleteFavorite: (customerId: number) => void,
  changeOrder: (draggedItem: ICustomerWithFavorites, newIndex: number) => void
}

const ModalCustomerFavorites = ({
  list,
  isLoading,
  createFavorite,
  deleteFavorite,
  changeOrder
}: IModalCustomerFavoritesProps) => {

  const [search, setSearch] = useState<string>('')
  const debouncedSearch = useDebounce(search, 500)
  const [isDragging, setIsDragging] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const autoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const filteredList = useMemo(() => {
    return list.filter(item => item.Customer_Name.trim().toLowerCase().includes(debouncedSearch.toLowerCase()))
  }, [list, debouncedSearch])

  const itemIds = useMemo(() => filteredList.map(item => item.CustomerID.toString()), [filteredList])

  const activeItem = useMemo(() => {
    if (!activeId) return null
    return filteredList.find(item => item.CustomerID.toString() === activeId) ?? null
  }, [activeId, filteredList])

  const propsElement = {
    isLoading,
    createFavorite,
    deleteFavorite
  }

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true)
    setActiveId(String(event.active.id))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setIsDragging(false)
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const sourceIndex = filteredList.findIndex(item => item.CustomerID.toString() === active.id)
    const destIndex = filteredList.findIndex(item => item.CustomerID.toString() === over.id)
    if (sourceIndex < 0 || destIndex < 0) return
    const draggedItem = filteredList[sourceIndex]
    changeOrder(draggedItem, destIndex)
  }

  const handleDragCancel = () => {
    setIsDragging(false)
    setActiveId(null)
  }

  // Автоскролл при приближении к краям
  useEffect(() => {
    if (!isDragging || !scrollContainerRef.current) return

    const handleScroll = (clientY: number) => {
      const container = scrollContainerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const scrollZone = 100
      const scrollSpeed = 25

      const positionY = clientY - rect.top
      const distanceFromTop = positionY
      const distanceFromBottom = rect.height - positionY


      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current)
        autoScrollIntervalRef.current = null
      }

      if (distanceFromTop < scrollZone && distanceFromTop > 0) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (container.scrollTop > 0) {
            container.scrollTop -= scrollSpeed
          }
        }, 50)
      }
      else if (distanceFromBottom < scrollZone && distanceFromBottom > 0) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (container.scrollTop < container.scrollHeight - container.clientHeight) {
            container.scrollTop += scrollSpeed
          }
        }, 50)
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleScroll(e.clientY)
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        handleScroll(e.touches[0].clientY)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('touchmove', handleTouchMove, { passive: true })

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('touchmove', handleTouchMove)
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current)
      }
    }
  }, [isDragging])

  return (
    <MainWrapper>
      <ContentWrapper>
      <SearchInput
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder='חיפוש לקוח'
        showKbd={false}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <Wrapper
            ref={scrollContainerRef}
            $isDraggingOver={isDragging}
          >
            {filteredList.map((item) => (
              <CustomerFavoritesItem
                key={item.CustomerID}
                {...item}
                {...propsElement}
              />
            ))}
          </Wrapper>
        </SortableContext>
        <DragOverlay>
          {activeItem ? (
            <CloneWrapper>
              <CloneItem>
                {activeItem.Customer_Name.trim()}
              </CloneItem>
              <CloneItem $isFavorite={activeItem.isFavorite}>
                <StarIcon fill />
              </CloneItem>
            </CloneWrapper>
          ) : null}
        </DragOverlay>
      </DndContext>
      </ContentWrapper>

    </MainWrapper>
  )
}
const MainWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
  }
`
const ContentWrapper = styled.div`
  width: 90%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  position: relative;
`
const Wrapper = styled.div<{ $isDraggingOver?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  overflow-y: auto;
  overflow-x: hidden;

  padding: ${({ $isDraggingOver }) => $isDraggingOver ? '0.5em' : '0'};
  border: ${({ $isDraggingOver }) =>
    $isDraggingOver ? '2px dashed #667eea' : '2px dashed transparent'};
  border-radius: ${({ $isDraggingOver }) => $isDraggingOver ? '15px' : '0'};
  background-color: ${({ $isDraggingOver }) =>
    $isDraggingOver ? 'rgba(102, 126, 234, 0.05)' : 'transparent'};
  transition: all 0.2s ease;

  &::-webkit-scrollbar {
    width: 0;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`
const CloneWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4em;
  border-radius: 30px;
  background-color: #fff;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  opacity: 0.9;
  transform: rotate(2deg);
`
const CloneItem = styled.div<{ $isFavorite?: boolean }>`
  cursor: pointer;
  color: ${({ $isFavorite }) => $isFavorite ? '#FFCA28' : 'gray'};
`
export default ModalCustomerFavorites
;